import { useContext } from 'react';

import { Button, Card, Input, Notice, Form, TextLink } from '@upside/flux-ui';

import { zodResolver } from '@hookform/resolvers/zod';
import { AppContext } from 'context/App';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { KRAKEN_SUPPORT_EMAIL } from '../../constants';

export function ForgotPassword() {
  const context = useContext(AppContext);

  const { t: translate } = useTranslation(['forgotPassword', 'login']);

  const formSchema = z.object({
    email: z
      .string({
        error: translate('forgotPassword:errorInvalidUsername'),
      })
      .min(1, translate('forgotPassword:errorInvalidUsername'))
      .email(translate('forgotPassword:errorInvalidUsername')),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(formSchema),
  });

  const { formState, getValues, setError } = form;

  const { isSubmitting, errors } = formState;
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const { email } = data;
    try {
      if (context && context.authentication.triggerForgottenPassword) {
        await context.authentication.triggerForgottenPassword({ email });
      }
    } catch (err: any) {
      const { message } = err;
      if (message.includes('429')) {
        setError('root', {
          message: translate('forgotPassword:errorTooManyRequests'),
        });
      } else {
        setError('root', { message: translate('forgotPassword:error') });
      }
    }
  };

  return (
    <div className="my-auto flex w-full flex-col items-center gap-8">
      {form.formState.isSubmitSuccessful && (
        <Card className="relative flex w-full max-w-lg flex-col items-center gap-8 rounded-xl p-8 shadow-md">
          <h1 className="text-center text-2xl font-medium text-foreground">
            {translate('forgotPassword:checkEmail')}
          </h1>
          <p className="text-center">
            <Trans
              i18nKey="forgotPassword:sentResetLinkTo"
              t={translate}
              values={{
                username: getValues('email'),
                supportEmail: KRAKEN_SUPPORT_EMAIL,
              }}
              components={{
                noBreak: <span className="break-keep" />,
              }}
            />
          </p>
        </Card>
      )}
      {!form.formState.isSubmitSuccessful && (
        <Card className="relative flex w-full max-w-md flex-col items-center gap-8 rounded-xl p-8 shadow-md">
          <div className="flex flex-col gap-4">
            <h1 className="text-center text-2xl font-medium text-foreground">
              {translate('forgotPassword:forgotPassword')}
            </h1>
            <p className="text-center">
              {translate('forgotPassword:enterUsername')}
            </p>
          </div>
          {errors.root && (
            <Notice intent="error" className="w-full">
              <Notice.Content>
                <Notice.Icon />
                <Notice.Caption>{errors.root.message}</Notice.Caption>
              </Notice.Content>
            </Notice>
          )}
          <Form {...form}>
            <form
              className="flex w-full flex-col gap-8"
              onSubmit={form.handleSubmit((data) => onSubmit(data))}
            >
              <div className="flex flex-col gap-2">
                <Form.Field
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <Form.Item>
                      <Form.Label>{translate('login:username')}</Form.Label>
                      <Form.Control>
                        <Input
                          size="xl"
                          {...field}
                          disabled={isSubmitting}
                          autoComplete="off"
                        />
                      </Form.Control>
                      <Form.Message />
                    </Form.Item>
                  )}
                />
                <p className="text-sm text-foreground-secondary">
                  {translate('forgotPassword:usernameInfo')}
                </p>
              </div>
              <Button
                className="w-full justify-center"
                loading={isSubmitting}
                size="lg"
                intent="primary"
                type="submit"
              >
                <Button.Label>
                  {translate('forgotPassword:submit')}
                </Button.Label>
              </Button>
            </form>
          </Form>
        </Card>
      )}
      <TextLink asChild className="z-0">
        <Link to="/login">
          <TextLink.Label>
            {translate('forgotPassword:backToLogin')}
          </TextLink.Label>
        </Link>
      </TextLink>
    </div>
  );
}
